/**
 * v3.20.0 — /forgot-password page.
 *
 * Accepts an email, calls POST /api/auth/forgot-password, and shows the
 * generic "If that email exists, a link has been sent…" message
 * regardless of outcome — backend already handles enumeration safety,
 * we just mirror the language.
 */
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { describeApiError, readFetchError } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPasswordPage() {
  // Prefilled when arriving from the login page's "Forgot password?" link,
  // which passes the address via router state (never a query param).
  const location = useLocation();
  const [email, setEmail] = useState(location.state?.email || '');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!email.trim()) {
      setError('Please enter your email.');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (!res.ok) {
        const msg = await readFetchError(res, `HTTP ${res.status}`);
        throw new Error(msg);
      }
      setSubmitted(true);
    } catch (err) {
      setError(describeApiError(err, 'Could not submit request. Try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md border-slate-200 shadow-sm" data-testid="forgot-password-card">
        <CardHeader className="space-y-1">
          <CardTitle
            className="text-2xl font-semibold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Reset your password
          </CardTitle>
          <p className="text-sm text-slate-500">
            Enter the email you use to sign in. We'll send you a link to set a new password.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {submitted ? (
            <div
              className="rounded-lg border border-emerald-200 bg-emerald-50 p-4"
              data-testid="forgot-password-success"
            >
              <div className="flex items-start gap-3">
                <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-emerald-900">
                  <p className="font-medium mb-1">Check your inbox.</p>
                  <p>
                    If an account exists for <strong>{email}</strong>, we just sent a password
                    reset link. It expires in 1 hour and can only be used once. Don't see it?
                    Check your spam folder.
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-sm font-medium text-teal-700 hover:text-teal-800"
                  data-testid="back-to-login-link"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to sign in
                </Link>
              </div>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-700">
                  Email address
                </Label>
                <div className="relative">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setError(null);
                    }}
                    autoFocus
                    autoComplete="email"
                    className="pl-9 border-slate-200 focus:border-teal-600 focus:ring-teal-600/20"
                    data-testid="forgot-password-email-input"
                  />
                </div>
              </div>

              {error && (
                <div
                  className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700"
                  data-testid="forgot-password-error"
                >
                  {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                data-testid="forgot-password-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending…
                  </>
                ) : (
                  'Send reset link'
                )}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="inline-flex items-center gap-1 text-sm text-teal-700 hover:text-teal-800 hover:underline"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
